import type { AuthProvider } from "ra-core";

import {
  apiFetch,
  fileUrl,
  getSupabaseClient,
  sessionFromResponse,
  tokenStore,
  type TokenResponse,
} from "./client";

export type VvRole = "owner" | "head" | "manager";

type CurrentSale = {
  id: number;
  first_name: string;
  last_name: string;
  role: VvRole;
  department: string | null;
  /** personal rights (crm/db/12_projects.sql) */
  can_delete?: boolean;
  can_export?: boolean;
  administrator?: boolean;
  avatar?: { src?: string } | null;
};

type Me = {
  id: string;
  email: string;
  sales: Omit<CurrentSale, "administrator" | "avatar">;
};

// The current sale is cached so that every permission check does not hit the
// server. It is cleared on login and logout.
const CURRENT_SALE_KEY = "vv.auth.current_sale";

const readCachedSale = (): CurrentSale | null => {
  try {
    const raw = localStorage.getItem(CURRENT_SALE_KEY);
    return raw ? (JSON.parse(raw) as CurrentSale) : null;
  } catch {
    return null;
  }
};

const clearCache = () => {
  try {
    localStorage.removeItem(CURRENT_SALE_KEY);
  } catch {
    // ignore
  }
};

const loadSale = async (): Promise<CurrentSale | null> => {
  const me = await apiFetch<Me>("/api/auth/me");
  let sale: CurrentSale = { ...me.sales };
  // the avatar is only in the sales row
  const { data } = await getSupabaseClient()
    .from("sales")
    .select("id, first_name, last_name, avatar, administrator")
    .eq("id", me.sales.id)
    .maybeSingle();
  if (data) sale = { ...data, ...me.sales, avatar: data.avatar };
  try {
    localStorage.setItem(CURRENT_SALE_KEY, JSON.stringify(sale));
  } catch {
    // ignore
  }
  return sale;
};

// The owner may change role and personal rights at any time: the cached sale
// is refreshed in the background once per page load (a reload picks the
// change up without logging out). The database enforces the rights anyway.
let refreshedThisLoad = false;

const getSale = async (): Promise<CurrentSale | null> => {
  if (!tokenStore.read()) return null;
  const cached = readCachedSale();
  if (!cached) {
    refreshedThisLoad = true;
    return loadSale();
  }
  if (!refreshedThisLoad) {
    refreshedThisLoad = true;
    loadSale().catch(() => undefined);
  }
  return cached;
};

/** Signs in with email and password and stores the session for all tabs. */
export const signIn = async (email: string, password: string) => {
  const response = await apiFetch<TokenResponse>("/api/auth/login", {
    method: "POST",
    json: { email, password },
    auth: false,
  });
  tokenStore.save(sessionFromResponse(response));
};

/*
 * "Logging out" state. ra-core's logout() navigates to /login inside a React
 * transition and then resets the store and the query cache. If checkAuth,
 * getPermissions or canAccess answer at once while the old routes are still
 * mounted (an anonymous answer needs no I/O), the app routes re-render their
 * LogoutOnMount, which logs out again before the navigation commits, forever
 * (white screen, "Maximum update depth exceeded"). So from logout() until the
 * login page is on screen (it calls notifyLoginPageShown) or the next login,
 * these three calls wait: the routes show their loader instead. A fallback
 * ends the state after 10 s in case the login page never shows.
 */
const LOGGING_OUT_FALLBACK_MS = 10 * 1000;
let loggingOut: { done: Promise<void>; release: () => void } | null = null;

const endLoggingOut = () => {
  const state = loggingOut;
  loggingOut = null;
  state?.release();
};

const beginLoggingOut = () => {
  if (loggingOut) return;
  let release!: () => void;
  const done = new Promise<void>((resolve) => (release = resolve));
  const state = { done, release };
  loggingOut = state;
  setTimeout(() => {
    if (loggingOut === state) endLoggingOut();
  }, LOGGING_OUT_FALLBACK_MS);
};

const afterLoggingOut = async () => {
  while (loggingOut) await loggingOut.done;
};

/** Called by the login page once it is mounted: logging out is over. */
export const notifyLoginPageShown = endLoggingOut;

/** Revokes a refresh token; failures are ignored (logging out locally is enough). */
const revoke = async (refreshToken: string) => {
  try {
    await apiFetch("/api/auth/logout", {
      method: "POST",
      json: { refresh_token: refreshToken },
      auth: false,
    });
  } catch {
    // ignore
  }
};

const isOnLoginPage = () =>
  window.location.hash.startsWith("#/login") ||
  window.location.pathname.endsWith("/login");

// Rights that only the owner has. "connections" is the settings page for
// external connections (telephony, Telegram…), coming in a later task.
const OWNER_ONLY_RESOURCES = ["sales", "configuration", "connections"];

// Projects, their pipelines and stages, and who works where: everyone reads
// their own (the database decides what), only the owner changes them.
const OWNER_EDITS_RESOURCES = [
  "projects",
  "pipelines",
  "pipeline_stages",
  "sales_projects",
];
const READ_ACTIONS = ["list", "show", "read"];

// The personal right «может удалять» (sales.can_delete) covers these records;
// the database refuses the delete without it. Notes and tasks are not covered.
const DELETE_RIGHT_RESOURCES = ["deals", "contacts", "companies", "leads"];

type SaleRights = Pick<CurrentSale, "role" | "can_delete" | "can_export">;

/**
 * What the current employee may do. "export" is the pseudo-resource of CSV /
 * JSON import and export and vCard: the personal right «может выгружать»
 * (sales.can_export). The owner may do everything.
 */
export const canAccessForSale = (
  sale: SaleRights | null | undefined,
  params: { resource: string; action: string },
) => {
  if (!sale) return false;
  if (sale.role === "owner") return true;
  if (OWNER_ONLY_RESOURCES.includes(params.resource)) return false;
  if (params.resource === "export") return sale.can_export === true;
  if (OWNER_EDITS_RESOURCES.includes(params.resource)) {
    return READ_ACTIONS.includes(params.action);
  }
  if (
    params.action === "delete" &&
    DELETE_RIGHT_RESOURCES.includes(params.resource)
  ) {
    return sale.can_delete === true;
  }
  return true;
};

export const getAuthProvider = (): AuthProvider => ({
  async login(params: { email?: string; username?: string; password: string }) {
    endLoggingOut();
    clearCache();
    tokenStore.clear();
    await signIn(params.email ?? params.username ?? "", params.password);
    try {
      await loadSale();
    } catch (error) {
      // signed in but without a profile: do not leave a half session behind
      const session = tokenStore.read();
      tokenStore.clear();
      clearCache();
      if (session) await revoke(session.refresh_token);
      throw error;
    }
  },
  async logout() {
    beginLoggingOut();
    const session = tokenStore.read();
    tokenStore.clear();
    clearCache();
    if (session) await revoke(session.refresh_token);
  },
  async checkAuth() {
    await afterLoggingOut();
    // resolves with a stale token when crm-api is unreachable (the session is
    // kept); rejects only without a session or when the refresh was refused
    const token = await tokenStore.get();
    if (!token) {
      clearCache();
      throw new Error("Not authenticated");
    }
  },
  async checkError(error: { status?: number }) {
    // 403 means "not allowed" (RLS, owner-only action): stay signed in.
    if (error?.status !== 401) return;
    // 401: the token was refused, or the session is gone (refresh refused,
    // logged out in another tab) and the request went out anonymous.
    // On the login page there is nobody to log out; rejecting would redirect
    // to the login page again and again.
    if (isOnLoginPage()) {
      tokenStore.clear();
      clearCache();
      return;
    }
    // The access token expired and the refresh failed for a transient reason
    // (crm-api unreachable): keep the session, the next request tries again.
    if (tokenStore.isExpired()) return;
    // ra-core then calls logout(), which revokes the refresh token and
    // clears the session
    throw new Error("Session expired");
  },
  async getIdentity() {
    const sale = await getSale();
    if (!sale) throw new Error("Not authenticated");
    return {
      id: sale.id,
      fullName: `${sale.first_name} ${sale.last_name}`,
      avatar: fileUrl(sale.avatar?.src),
    };
  },
  async getPermissions() {
    await afterLoggingOut();
    const sale = await getSale();
    return sale?.role;
  },
  async canAccess(params) {
    await afterLoggingOut();
    const sale = await getSale();
    return canAccessForSale(sale, params);
  },
});
